import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import type { ShapeTransformSnapshot } from './types/shape-transform-snapshot'

export function isDescendantOfGroup(
  shape: ShapeData,
  groupId: string,
  byId: Map<string, ShapeData>
): boolean {
  let parentId = shape.parentGroupId

  while (parentId) {
    if (parentId === groupId) return true
    parentId = byId.get(parentId)?.parentGroupId
  }

  return false
}

export function collectGroupTransformInitials(
  list: ShapeData[],
  groupId: string
): Record<string, ShapeTransformSnapshot> {
  const byId = new Map(list.map((shape) => [shape.id, shape]))
  const snapshots: Record<string, ShapeTransformSnapshot> = {}

  for (const shape of list) {
    if (shape.id !== groupId && !isDescendantOfGroup(shape, groupId, byId)) continue
    snapshots[shape.id] = {
      x: shape.x,
      y: shape.y,
      width: shape.width,
      height: shape.height,
      rotation: shape.rotation ?? 0,
    }
  }

  return snapshots
}

export function collectNestedGroupIdsPostOrder(list: ShapeData[], rootGroupId: string): string[] {
  const childrenByParent = new Map<string, string[]>()

  for (const shape of list) {
    if (!shape.parentGroupId) continue
    const existing = childrenByParent.get(shape.parentGroupId) ?? []

    existing.push(shape.id)
    childrenByParent.set(shape.parentGroupId, existing)
  }

  const seen = new Set<string>()
  const ordered: string[] = []
  const visit = (gid: string) => {
    if (seen.has(gid)) return
    seen.add(gid)
    const childIds = childrenByParent.get(gid) ?? []

    for (const childId of childIds) {
      const child = list.find((shape) => shape.id === childId)

      if (child && isGroupShape(child)) {
        visit(child.id)
      }
    }

    ordered.push(gid)
  }

  visit(rootGroupId)
  return ordered
}

/**
 * Every non-group (leaf) shape and text box descending from `groupId`, at any nesting depth —
 * recurses through nested child groups regardless of THEIR own settings, so a leaf's real
 * geometry is always reached. Used by group "Wrap Shape" decomposition
 * (`compute-wrap-obstacles.ts`): a group set to Wrap Shape treats each of these leaves as its own
 * independent contour/rect obstacle, rather than the group's single outer bounding box, so text
 * can flow into empty space between/around individual members.
 */
export function collectLeafDescendants(
  shapes: ShapeData[],
  groupId: string,
  textBoxes: TextBoxData[] = []
): { shapes: ShapeData[]; textBoxes: TextBoxData[] } {
  const shapeChildrenByParent = new Map<string, ShapeData[]>()

  for (const s of shapes) {
    if (!s.parentGroupId) continue
    const existing = shapeChildrenByParent.get(s.parentGroupId) ?? []

    existing.push(s)
    shapeChildrenByParent.set(s.parentGroupId, existing)
  }

  const textChildrenByParent = new Map<string, TextBoxData[]>()

  for (const t of textBoxes) {
    if (!t.parentGroupId) continue
    const existing = textChildrenByParent.get(t.parentGroupId) ?? []

    existing.push(t)
    textChildrenByParent.set(t.parentGroupId, existing)
  }

  const resultShapes: ShapeData[] = []
  const resultTexts: TextBoxData[] = []
  const visit = (gid: string) => {
    for (const child of shapeChildrenByParent.get(gid) ?? []) {
      if (isGroupShape(child)) {
        visit(child.id)
      } else {
        resultShapes.push(child)
      }
    }

    resultTexts.push(...(textChildrenByParent.get(gid) ?? []))
  }

  visit(groupId)
  return { shapes: resultShapes, textBoxes: resultTexts }
}
